import { type MutableRefObject, useCallback } from 'react'

/** Client ↔ canvas coordinate mapping over the scroller and the document canvas. */
export function useViewportCoordinates({
  scrollRef,
  documentCanvasRef,
  scale,
}: {
  scrollRef: MutableRefObject<HTMLDivElement | null>
  documentCanvasRef: MutableRefObject<HTMLDivElement | null>
  scale: number
}) {
  /** Map scroller + canvas layout to the canvas origin in scroller content coordinates (used by pointers, rulers, zoom helpers). */
  const getCanvasOrigin = useCallback(() => {
    const scroller = scrollRef.current
    const canvasEl = documentCanvasRef.current

    if (!scroller || !canvasEl) return { originX: 0, originY: 0 }
    const scRect = scroller.getBoundingClientRect()
    const canvasRect = canvasEl.getBoundingClientRect()
    const originX = scroller.scrollLeft + (canvasRect.left - scRect.left)
    const originY = scroller.scrollTop + (canvasRect.top - scRect.top)

    return { originX, originY }
  }, [scrollRef, documentCanvasRef])

  /**
   * Document-space (pt) point currently under a given screen position, at a given scale.
   * Must be called BEFORE the zoom-triggered scale change lands — the pasteboard resizes
   * with zoom (see `pasteboardSize` in `use-spread-layout`), and shrinking it can make the
   * browser auto-clamp `scrollLeft`/`scrollTop` if they'd otherwise exceed the new (smaller)
   * scroll range. Capturing the content point up front avoids re-deriving it from a scroll
   * position that may have already been silently clamped by the time the zoom's layout effect runs.
   */
  const computeContentPointAtClient = useCallback(
    (clientX: number, clientY: number, atScale: number) => {
      const scroller = scrollRef.current

      if (!scroller) return null

      const scRect = scroller.getBoundingClientRect()
      const cx = clientX - scRect.left
      const cy = clientY - scRect.top
      const { originX, originY } = getCanvasOrigin()

      return {
        contentX: (scroller.scrollLeft + cx - originX) / atScale,
        contentY: (scroller.scrollTop + cy - originY) / atScale,
      }
    },
    [getCanvasOrigin, scrollRef]
  )

  /** Document-space point under a pointer at the current scale; null before the canvas mounts. */
  const getCanvasPointFromPointerEvent = useCallback(
    (clientX: number, clientY: number) => {
      const scroller = scrollRef.current
      const canvasEl = documentCanvasRef.current

      if (!scroller || !canvasEl) return null

      const { originX, originY } = getCanvasOrigin()
      const rect = scroller.getBoundingClientRect()
      const px = clientX - rect.left + scroller.scrollLeft
      const py = clientY - rect.top + scroller.scrollTop

      return {
        canvasX: (px - originX) / scale,
        canvasY: (py - originY) / scale,
      }
    },
    [getCanvasOrigin, scale, scrollRef, documentCanvasRef]
  )

  return { getCanvasOrigin, computeContentPointAtClient, getCanvasPointFromPointerEvent }
}
